import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { translate } from '@smovilgps/optimus-intl';
import { Button, message, Popconfirm, Space, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useAppDispatch } from '@/OptimusRoutes/hooks/redux.hooks';
import { RootState } from '@/store/configureStore';
import CommonTable from '../common-components/CommonTable';
import { GetModelProfiles } from '../modelProfile/actions';
import { ModelProfileDto } from '../modelProfile/modelProfile.dto';
import { StripePlans } from '../stripeRoutes/entities/stripePlans.entity';
import AddOrEditModal from './AddOrEditModal';
import { BILLING_INTERVAL, PAYMENT_METHOD } from './constants';
import StripePlansService from './stripePlan.service';
const { Text } = Typography;

type StripePlanData = {
	data: StripePlans[];
	count: number;
};

const StripePlansPage = () => {
	const intl = useIntl();
	const dispatch = useAppDispatch();
	const [loading, setLoading] = useState<boolean>(false);
	const [showModal, setShowModal] = useState<boolean>(false);
	const [recordToEdit, setRecordToEdit] = useState<StripePlans | null>(null);
	const [stripePlan, setStripePlan] = useState<StripePlanData>({
		data: [],
		count: 0,
	});
	const modelProfiles = useSelector((state: RootState) => state.modelProfile.profiles);

	useEffect(() => {
		dispatch(GetModelProfiles());
		(async () => {
			await doPaging();
		})();
	}, []);

	const paymentMethodType = (type: string) => {
		if (type === PAYMENT_METHOD.PUNTUAL.key) {
			return intl.formatMessage(translate(PAYMENT_METHOD.PUNTUAL.message));
		} else if (type === PAYMENT_METHOD.RECURRING.key) {
			return intl.formatMessage(translate(PAYMENT_METHOD.RECURRING.message));
		}
	};

	const billingInterval = (interval: string | undefined) => {
		if (interval === BILLING_INTERVAL.DAILY.key) {
			return intl.formatMessage(translate(BILLING_INTERVAL.DAILY.message));
		} else if (interval === BILLING_INTERVAL.MONTHLY.key) {
			return intl.formatMessage(translate(BILLING_INTERVAL.MONTHLY.message));
		} else if (interval === BILLING_INTERVAL.ANNUAL.key) {
			return intl.formatMessage(translate(BILLING_INTERVAL.ANNUAL.message));
		} else {
			return '-';
		}
	};

	const onDeletePlan = async (id: number) => {
		try {
			setLoading(true);
			await StripePlansService.deleteStripePlan(id);
			await doPaging();
		} catch (error) {
			message.error(intl.formatMessage(translate('commonError')));
		} finally {
			setLoading(false);
		}
	};

	const columns: ColumnsType<StripePlans> = [
		{
			title: intl.formatMessage(translate('model')),
			dataIndex: 'modelProfileId',
			key: 'modelProfileId',
			width: 150,
			render: (_, { modelProfileId }) => {
				const modelProfile = modelProfiles.find((model: ModelProfileDto) => model.id === modelProfileId);
				return <Text style={{ maxWidth: '100%' }}>{modelProfile?.description ?? '-'}</Text>;
			},
		},
		{
			title: intl.formatMessage(translate('description')),
			dataIndex: 'description',
			key: 'description',
			width: 150,
			render: (_, { description }) => <Text style={{ maxWidth: '100%' }}>{description}</Text>,
		},
		{
			title: intl.formatMessage(translate('price')),
			dataIndex: 'amount',
			key: 'amount',
			width: 60,
			render: (_, { price }) => (
				<Text style={{ maxWidth: '100%' }}>{`$ ${price?.amount ? price?.amount / 100 : 0}`}</Text>
			),
		},
		{
			title: intl.formatMessage(translate('paymentMethod')),
			dataIndex: 'type',
			key: 'type',
			width: 60,
			render: (_, { price }) => <Text style={{ maxWidth: '100%' }}>{paymentMethodType(price.type)}</Text>,
		},
		{
			title: intl.formatMessage(translate('billingPeriod')),
			children: [
				{
					title: intl.formatMessage(translate('frequency')),
					dataIndex: 'intervalCount',
					key: 'intervalCount',
					width: 60,
					render: (_, { price }) => <Text style={{ maxWidth: '100%' }}>{price?.intervalCount ?? '-'}</Text>,
				},
				{
					title: intl.formatMessage(translate('interval')),
					dataIndex: 'interval',
					key: 'interval',
					width: 60,
					render: (_, { price }) => (
						<Text style={{ maxWidth: '100%' }}>{billingInterval(price?.interval)}</Text>
					),
				},
			],
		},
		{
			title: intl.formatMessage(translate('status')),
			dataIndex: 'status',
			key: 'status',
			width: 100,
			align: 'center',
			render: (_, { status }) => (
				<Tag color={status ? 'green' : 'red'}>
					{status ? intl.formatMessage(translate('active')) : intl.formatMessage(translate('inactive'))}
				</Tag>
			),
		},
		{
			title: intl.formatMessage(translate('mode')),
			dataIndex: 'isTest',
			key: 'isTest',
			width: 100,
			align: 'center',
			render: (_, { isTest }) => (
				<Tag color={isTest ? 'blue' : 'default'}>
					{isTest ? intl.formatMessage(translate('test')) : intl.formatMessage(translate('live'))}
				</Tag>
			),
		},
		{
			title: intl.formatMessage(translate('actions')),
			key: 'actions',
			width: 100,
			align: 'center',
			render: (_, record) => (
				<Space>
					<Button type="link" icon={<EditOutlined />} onClick={() => editStripePlanRecord(record)} />
					<Popconfirm
						title={intl.formatMessage(translate('deleteConfirm'))}
						onConfirm={() => onDeletePlan(record.id)}
						okText={intl.formatMessage(translate('yes'))}
						cancelText={intl.formatMessage(translate('no'))}
					>
						<Button type="link" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	const editStripePlanRecord = (record: StripePlans) => {
		setRecordToEdit(record);
		setShowModal(true);
	};

	const doPaging = async (search = '', page = 1, pageSize = 15) => {
		setLoading(true);
		try {
			const { data, count } = await StripePlansService.getStripePlans(search, page, pageSize);
			setStripePlan({ data, count });
		} catch (error) {
			message.error(intl.formatMessage(translate('commonError')));
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<CommonTable
				columns={columns}
				dataSource={stripePlan.data}
				loading={loading}
				onSearch={(search, page, pageSize) => doPaging(search, page, pageSize)}
				recordCount={stripePlan.count}
				Add={
					<Button
						className="editable-add-btn"
						type="primary"
						onClick={() => {
							setRecordToEdit(null);
							setShowModal(true);
						}}
					>
						<PlusOutlined />
						<FormattedMessage {...translate('add')} />
					</Button>
				}
				getCheckboxProps={() => ({ disabled: true })}
				hideDelete
			/>
			<AddOrEditModal
				showModal={showModal}
				title={
					recordToEdit !== null
						? intl.formatMessage(translate('editPlan'))
						: intl.formatMessage(translate('addPlan'))
				}
				onCancel={() => {
					setRecordToEdit(null);
					setShowModal(false);
				}}
				stripePlan={recordToEdit}
				retrieveStripePlans={() => doPaging()}
			/>
		</>
	);
};

export default StripePlansPage;
